'use client';

import React, { useState, useEffect } from 'react';
import BlogCard from './BlogCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import FeaturedPost from './FeaturedPost';
import NewsletterSignup from './NewsletterSignup';
import PopularPosts from './PopularPosts';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  slug: string;
  featured?: boolean;
}

interface Category {
  id: string;
  name: string;
  count: number;
}

interface BlogInteractiveProps {
  initialPosts: BlogPost[];
  categories: Category[];
  popularPosts: BlogPost[];
}

const BlogInteractive: React.FC<BlogInteractiveProps> = ({
  initialPosts,
  categories,
  popularPosts,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState(initialPosts);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = initialPosts;

    if (activeCategory !== 'all') {
      filtered = filtered.filter((post) => post.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query) ||
          post.category.toLowerCase().includes(query)
      );
    }

    setFilteredPosts(filtered);
  }, [activeCategory, searchQuery, initialPosts, isHydrated]);

  const featuredPost = initialPosts.find((post) => post.featured);
  const regularPosts = filteredPosts.filter((post) => !post.featured);

  if (!isHydrated) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-96 bg-muted rounded-lg"></div>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-6">
              <div className="h-64 bg-muted rounded-lg"></div>
              <div className="h-96 bg-muted rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
      {featuredPost && <FeaturedPost post={featuredPost} />}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search articles by title, content, or category..."
          />

          {regularPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {regularPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-lg p-12 text-center shadow-subtle">
              <p className="font-body text-muted-foreground text-lg">
                No articles found matching your criteria. Try adjusting your filters or search query.
              </p>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />

          <PopularPosts posts={popularPosts} />

          <NewsletterSignup />
        </aside>
      </div>
    </div>
  );
};

export default BlogInteractive;